import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchIdentitaetenApi,
  createIdentitaetApi,
  updateIdentitaetApi,
  deleteIdentitaetApi,
} from './identitaetenApi';
import { verschluessleJson, entschluessleJson } from 'core/features/sicherheit/crypto';
import { holeMasterKey } from 'core/features/sicherheit/vaultHolder';
import { sperren } from 'core/features/sicherheit/sicherheitSlice';
import { logout } from 'core/features/auth/authSlice';

/**
 * Redux-Slice fuer das Identitaeten-Modul. Haelt die im Browser
 * ENTSCHLUESSELTEN Identitaeten (nur im Speicher). Der Master-Key liegt
 * Portal-weit im core-Feature "sicherheit". Alle Thunks ver-/entschluesseln an
 * der Grenze zur API; beim Sperren/Abmelden werden die Inhalte verworfen.
 */

// Inhaltliche Felder einer Identitaet, die verschluesselt abgelegt werden.
const inhaltVon = (ident) => ({
  dienst: ident.dienst || '',
  link: ident.link || '',
  email: ident.email || '',
  benutzername: ident.benutzername || '',
  passwort: ident.passwort || '',
  notiz: ident.notiz || '',
});

/** Entschluesselt ein Identitaets-DTO in ein Anzeige-Objekt. */
const entschluessleIdentitaet = async (mk, dto) => {
  const inhalt = await entschluessleJson(mk, dto.cipherData, dto.iv);
  return {
    uuid: dto.uuid,
    sortierung: dto.sortierung,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    ...inhaltVon(inhalt),
  };
};

/** Baut das verschluesselte Wire-DTO aus einem Anzeige-Objekt. */
const baueDto = async (mk, ident) => {
  const blob = await verschluessleJson(mk, inhaltVon(ident));
  return { cipherData: blob.cipherText, iv: blob.iv, sortierung: ident.sortierung || 0 };
};

// ── Thunks ──────────────────────────────────────────────
export const ladeIdentitaeten = createAsyncThunk('identitaeten/laden', async () => {
  const dtos = await fetchIdentitaetenApi();
  const mk = holeMasterKey();
  return Promise.all((dtos ?? []).map((d) => entschluessleIdentitaet(mk, d)));
});

export const speichereIdentitaet = createAsyncThunk('identitaeten/speichern', async (ident) => {
  const mk = holeMasterKey();
  const dto = await baueDto(mk, ident);
  const gespeichert = ident.uuid ? await updateIdentitaetApi(ident.uuid, dto) : await createIdentitaetApi(dto);
  return {
    uuid: gespeichert.uuid,
    sortierung: gespeichert.sortierung,
    createdAt: gespeichert.createdAt,
    updatedAt: gespeichert.updatedAt,
    ...inhaltVon(ident),
  };
});

export const loescheIdentitaet = createAsyncThunk('identitaeten/loeschen', async (uuid) =>
  deleteIdentitaetApi(uuid)
);

const initialState = {
  identitaeten: [],
  status: 'idle',
  error: null,
};

/** Verwirft alle entschluesselten Inhalte (beim Sperren/Abmelden). */
const leereInhalte = (state) => {
  state.identitaeten = [];
  state.status = 'idle';
  state.error = null;
};

/** Ersetzt ein Element (per uuid) in der Liste oder haengt es an. */
const upsert = (liste, element) => {
  const idx = liste.findIndex((e) => e.uuid === element.uuid);
  if (idx >= 0) liste[idx] = element;
  else liste.push(element);
};

const identitaetenSlice = createSlice({
  name: 'identitaeten',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(sperren, leereInhalte)
      .addCase(logout, leereInhalte)
      .addCase(ladeIdentitaeten.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(ladeIdentitaeten.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.identitaeten = action.payload;
      })
      .addCase(ladeIdentitaeten.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(speichereIdentitaet.fulfilled, (state, action) => {
        upsert(state.identitaeten, action.payload);
      })
      .addCase(loescheIdentitaet.fulfilled, (state, action) => {
        state.identitaeten = state.identitaeten.filter((i) => i.uuid !== action.payload);
      });
  },
});

// ── Selektoren ──────────────────────────────────────────
export const selectIdentitaeten = (state) => state.identitaeten.identitaeten;
export const selectIdentitaetenStatus = (state) => state.identitaeten.status;

export default identitaetenSlice.reducer;
